<template>
  <form
    autocomplete="off"
    class="organization-branch-form"
    v-on:submit.prevent="onSubmit"
  >
    <input
      hidden="hidden"
      type="submit"
      v-bind:disabled="disabled">
    <span class="organization-branch-form__subtitle">Укажите ФИО руководителя</span>
    <div class="organization-branch-form__three-fields">
      <input-text
        placeholder="Фамилия"
        title="Фамилия"
        v-bind:violations="directorSurnameInputErrors"
        v-letter-capital
        v-model="directorSurname" />
      <input-text
        placeholder="Имя"
        title="Имя"
        v-bind:violations="directorNameInputErrors"
        v-letter-capital
        v-model="directorName" />
      <input-text
        placeholder="Отчество"
        title="Отчество"
        v-bind:violations="directorPatronymicInputErrors"
        v-letter-capital
        v-model="directorPatronymic" />
    </div>
    <span class="organization-branch-form__subtitle">Укажите данные клиники</span>
    <slot name="branch-name" />
    <div class="organization-branch-form__two-fields">
      <div class="organization-branch-form__select">
        <label for="clinic-city">Город</label>
        <v-autocomplete
          v-bind:aria-invalid="hasCityViolations"
          background-color="white"
          class="organization-branch-form-autocomplete"
          clearable
          hide-details
          id="clinic-city"
          height="40px"
          item-text="name"
          item-value="id"
          outlined
          placeholder="Город"
          required="required"
          v-bind:disabled="disabled"
          v-bind:items="cities"
          v-model="clinicCity"
        >
          <template v-slot:no-data>
            <p style="padding: 10px; margin:0;">Результатов нет</p>
          </template>
        </v-autocomplete>
        <p
          class="text__violation"
          role="alert"
          v-for="violationText in clinicCityInputErrors"
          v-bind:key="violationText"
        >{{ violationText }}</p>
      </div>
      <input-phone
        v-bind:violations="phoneNumberInputErrors"
        v-model="phoneNumber"
        v-bind:disabled="disabled" />
    </div>
    <div class="organization-branch-form__two-fields">
      <the-select
        title="Выберите правовую форму регистрации"
        placeholder="ООО/ИП"
        v-bind:disabled="disabled"
        v-bind:optional="true"
        v-bind:options="settingsOptions.rightFormRegistrationsOptions"
        v-bind:violations="rightFormRegistrationErrors"
        v-model="clinicRightFormRegistration"
        name="clinic_right_form_registration"
      />
      <input-text
        placeholder="Код налогового органа"
        title="Код налогового органа"
        v-bind:disabled="disabled"
        v-bind:violations="taxAuthorityCodeInputErrors"
        v-letter-capital
        v-mask="$options.maskOfTaxAuthorityCode"
        v-model="taxAuthorityCode" />
    </div>
    <input-text
      class="organization-branch-form__field"
      title="Укажите юридическое лицо"
      placeholder="ООО Стоматология/ИП Иванов И.И."
      v-bind:disabled="disabled"
      v-bind:violations="legalNameInputErrors"
      v-letter-capital
      v-model="legalName" />
    <input-text
      class="organization-branch-form__field"
      placeholder="Адрес"
      title="Адрес"
      v-bind:disabled="disabled"
      v-bind:violations="clinicAddressInputErrors"
      v-letter-capital
      v-model="clinicAddress" />
    <div class="organization-branch-form__three-fields">
      <input-text
        placeholder="ИНН"
        title="ИНН"
        v-bind:disabled="disabled"
        v-bind:violations="clinicINNInputErrors"
        v-letter-capital
        v-mask="$options.maskOfINN"
        v-model="clinicINN" />
      <input-text
        placeholder="КПП"
        title="КПП"
        v-bind:disabled="disabled"
        v-bind:violations="clinicKPPInputErrors"
        v-letter-capital
        v-mask="$options.maskOfKPP"
        v-model="clinicKPP" />
      <input-text
        placeholder="ОГРН/ОГРНИП"
        title="ОГРН/ОГРНИП"
        v-bind:disabled="disabled"
        v-bind:violations="clinicOGRNInputErrors"
        v-letter-capital
        v-mask="$options.maskOfOGRN"
        v-model="clinicOGRN" />
    </div>
    <div class="organization-branch-form__three-fields">
      <input-text
        placeholder="Серия и номер лицензии"
        maxlength="50"
        required
        title="Серия и номер лицензии"
        v-bind:disabled="disabled"
        v-bind:violations="licenseSerialAndNumberInputErrors"
        v-letter-capital
        v-model="licenseSerialAndNumber" />
      <input-text
        placeholder="Кем выдана лицензия"
        maxlength="200"
        required
        title="Кем выдана лицензия"
        v-bind:disabled="disabled"
        v-bind:violations="licenseIssuerInputErrors"
        v-model="licenseIssuer" />
      <input-date
        label="Дата выдачи лицензии"
        v-bind:disabled="disabled"
        v-bind:optional="false"
        v-bind:violations="licenseIssuedDateInputErrors"
        v-model="licenseIssuedDate" />
    </div>
    <the-select
      class="organization-branch-form__field"
      title="Выберите уровень конфиденциальности ?"
      v-bind:disabled="disabled"
      v-bind:hint="$options.confidentialityHint"
      v-bind:optional="false"
      v-bind:options="settingsOptions.egiszConfidentialities"
      v-bind:violations="confidentialityLevelErrors"
      v-model="confidentialityLevelId" />
    <section
      class="organization-branch-form__cashbox"
      v-if="showCashboxSettings"
    >
      <h3>Касса</h3>
      <the-select
        title="НДС по умолчанию"
        placeholder="Выберите НДС"
        v-bind:disabled="disabled"
        v-bind:options="settingsOptions.vatOptions"
        v-model="defaultVat" />
      <the-select
        title="Система налогообложения"
        placeholder="Выберите систему налогообложения"
        v-bind:disabled="disabled"
        v-bind:options="settingsOptions.snoOptions"
        v-model="sno" />
      <input-checkbox
        title="Торговый эквайринг"
        v-model="isTradeAcquiringEnabled"
        v-bind:disabled="disabled" />
    </section>
    <button
      class="organization-branch-form__submit"
      type="submit"
      v-if="needsSubmitButton"
    >Сохранить</button>
  </form>
</template>

<script>
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputDate from '@/components/InputDate.vue';
import InputPhone from '@/components/InputPhone.vue';
import InputText from '@/components/InputText.vue';
import TheSelect from '@/components/TheSelect.vue';
import dateMixin from '@/mixins/dateMixin';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  (vm.disabled !== true && cause in vm.violationCauses)
    ? vm.violationCauses[cause]
    : []
);

const confidentialityHint = `
<b>Обычный</b> - доступ имеют любые авторизованные лица, заинтересованные в данной информации. <br>
<b>Ограниченный</b> - разрешение доступа к документу только персоналу, который непосредственно участвует в оказании мед. помощи пациенту. <br>
<b>Крайне ограниченный</b> - необходимо получения перс. разрешения от организации, хранящей документ, для доступа к данным.
`;

/**
 * @description Форма редактирования филиала организации.
 * @type {Vue}
 */
export default {
  confidentialityHint,
  components: {
    InputCheckbox,
    InputDate,
    InputPhone,
    InputText,
    TheSelect,
  },
  computed: {
    clinicCityInputErrors: resolveInputErrors('clinic_city'),
    clinicAddressInputErrors: resolveInputErrors('clinic_address'),
    clinicINNInputErrors: resolveInputErrors('clinic_inn'),
    clinicKPPInputErrors: resolveInputErrors('clinic_kpp'),
    clinicOGRNInputErrors: resolveInputErrors('clinic_ogrn'),
    confidentialityLevelErrors: resolveInputErrors('egisz_confidentiality'),
    directorNameInputErrors: resolveInputErrors('director_name'),
    directorPatronymicInputErrors: resolveInputErrors('director_patronymic'),
    directorSurnameInputErrors: resolveInputErrors('director_surname'),
    legalNameInputErrors: resolveInputErrors('legal_name'),
    licenseIssuedDateInputErrors: resolveInputErrors('license_issued_date'),
    licenseIssuerInputErrors: resolveInputErrors('license_issuer'),
    licenseSerialAndNumberInputErrors: resolveInputErrors('license_serial_and_number'),
    /**
     * @description Наличие ошибок по городу.
     * @returns {boolean}
     */
    hasCityViolations() {
      return this.clinicCityInputErrors.length > 0;
    },
    /**
     * @description Нужна отправка по кнопке.
     */
    needsSubmitButton() {
      return this.submitTrigger === null;
    },
    phoneNumberInputErrors: resolveInputErrors('phone_number'),
    rightFormRegistrationErrors: resolveInputErrors('clinic_right_form_registration'),
    taxAuthorityCodeInputErrors: resolveInputErrors('tax_authority_code'),
    /**
     * @description Есть изменения.
     */
    isChanged() {
      return this.clinicAddress !== this.initialClinicAddress
          || this.clinicCity !== this.initialClinicCity
          || this.clinicINN !== this.initialClinicINN
          || this.clinicKPP !== this.initialClinicKPP
          || this.confidentialityLevelId !== this.initialConfidentialityLevelId
          || this.directorName !== this.initialDirectorName
          || this.directorSurname !== this.initialDirectorSurname
          || this.directorPatronymic !== this.initialDirectorPatronymic
          || this.isTradeAcquiringEnabled !== this.initialIsTradeAcquiringEnabled
          || this.legalName !== this.initialLegalName
          || this.licenseIssuedDate !== this.initialLicenseIssuedDate
          || this.licenseIssuer !== this.initialLicenseIssuer
          || this.licenseSerialAndNumber !== this.initialLicenseSerialAndNumber
          || this.phoneNumber !== this.initialPhoneNumber
          || this.defaultVat !== this.initialVat
          || this.taxAuthorityCode !== this.initialTaxAuthorityCode;
    },
  },
  data() {
    const confidentialityLevelId = this.initialConfidentialityLevelId || (
      this
        .settingsOptions
        .egiszConfidentialities
        .slice(0, 1)
        .map(({ value }) => value)
        .at(0) || ''
    );
    return {
      /**
       * @description Адрес клиники.
       * @type {string}
       */
      clinicAddress: this.initialClinicAddress,
      /**
       * @description Город клиники.
       * @type {string}
       */
      clinicCity: this.initialClinicCity,
      /**
       * ИНН клиники.
       * @type {string}
       */
      clinicINN: this.initialClinicINN,
      /**
       * КПП клиники.
       * @type {string}
       */
      clinicKPP: this.initialClinicKPP,
      /**
       * ОГРН/ОГРНИП клиники.
       * @type {string}
       */
      clinicOGRN: this.initialClinicOGRN,
      /**
       * @description Правовая форма клиники.
       * @type String
       */
      clinicRightFormRegistration: this.initialClinicRightFormRegistration,
      /**
       * @description Уровень конфиденциальности по ЕГИСЗ НСИ.
       * @type {string}
       */
      confidentialityLevelId,
      /**
       * @description НДС по умолчанию.
       * @type {string}
       */
      defaultVat: this.initialVat,
      /**
       * @description Имя руководителя.
       * @type {string}
       */
      directorName: this.initialDirectorName,
      /**
       * @description Отчество руководителя.
       * @type {string}
       */
      directorPatronymic: this.initialDirectorPatronymic,
      /**
       * @description Фамилия руководителя.
       * @type {string}
       */
      directorSurname: this.initialDirectorSurname,
      /**
       * @description Торговый эквайринг включен.
       * @type {boolean}
       */
      isTradeAcquiringEnabled: this.initialIsTradeAcquiringEnabled,
      /**
       * @description Юридическое лицо.
       * @type {string}
       */
      legalName: this.initialLegalName,
      /**
       * @description Дата выдачи лицензии.
       * @type {string}
       */
      licenseIssuedDate: this.initialLicenseIssuedDate,
      /**
       * @description Кем выдана лицензия.
       * @type {string}
       */
      licenseIssuer: this.initialLicenseIssuer,
      /**
       * @description Серия и номер лицензии.
       * @type {string}
       */
      licenseSerialAndNumber: this.initialLicenseSerialAndNumber,
      /**
       * @description Номер телефона.
       * @type {string}
       */
      phoneNumber: this.initialPhoneNumber,
      /**
       * @description Система налогообложения.
       * @type {string}
       */
      sno: this.initialSno,
      /**
       * @description Код налогового органа.
       * @type {string}
       */
      taxAuthorityCode: this.initialTaxAuthorityCode,
    };
  },
  /**
   * @description Маска ИНН.
   * @type {string}
   */
  maskOfINN: '############',
  /**
   * @description Маска КПП.
   * @type {string}
   */
  maskOfKPP: '#########',
  /**
   * @description Маска ОГРН/ОГРНИП Клиники
   * @type {string}
   */
  maskOfOGRN: '#################',
  /**
   * @description Маска кода налогового органа.
   * @type {string}
   */
  maskOfTaxAuthorityCode: '####',
  methods: {
    /**
     * @description Собрать данные.
     * @returns {object}
     */
    collectData() {
      return {
        clinicAddress: this.clinicAddress,
        clinicCity: this.clinicCity,
        clinicINN: this.clinicINN,
        clinicKPP: this.clinicKPP,
        clinicOGRN: this.clinicOGRN,
        clinicRightFormRegistration: this.clinicRightFormRegistration,
        defaultVat: this.defaultVat,
        directorName: this.directorName,
        directorPatronymic: this.directorPatronymic,
        directorSurname: this.directorSurname,
        egiszConfidentialityId: this.confidentialityLevelId,
        isTradeAcquiringEnabled: this.isTradeAcquiringEnabled,
        legalName: this.legalName,
        licenseIssuedDate: this.formatDateToISO(this.licenseIssuedDate),
        licenseIssuer: this.licenseIssuer,
        licenseSerialAndNumber: this.licenseSerialAndNumber,
        phoneNumber: this.phoneNumber,
        sno: this.sno,
        taxAuthorityCode: this.taxAuthorityCode,
      };
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const data = this.collectData();
      this.$emit('submit', data);
    },
    /**
     * @description Обработать отправку по триггеру.
     * @returns {void}
     */
    onSubmitTrigger() {
      const data = this.collectData();
      this.$emit('submit-by-trigger', this.submitTrigger, data);
    },
  },
  mixins: [dateMixin],
  name: 'FormOrganizationBranchEditing',
  props: {
    /**
     * @description Список городов.
     * @type {any[]}
     */
    cities: {
      required: true,
      type: Array,
    },
    /**
     * @description Отключена.
     */
    disabled: {
      required: true,
      type: Boolean,
    },
    initialClinicAddress: {
      default: () => '',
      type: String,
    },
    initialClinicCity: {
      default: () => '',
      type: String,
    },
    initialClinicINN: {
      default: () => '',
      type: String,
    },
    initialClinicKPP: {
      default: () => '',
      type: String,
    },
    /**
     * @description Начальное значение для ОГРН.
     * @type String
     */
    initialClinicOGRN: {
      default: () => '',
      type: String,
    },
    /**
     * @description Начальное значение для правовой формы регистрации.
     * @type String
     */
    initialClinicRightFormRegistration: {
      default: () => '',
      type: String,
    },
    initialConfidentialityLevelId: {
      default: () => '',
      type: String,
    },
    initialDirectorName: {
      default: () => '',
      type: String,
    },
    initialDirectorPatronymic: {
      default: () => '',
      type: String,
    },
    initialDirectorSurname: {
      default: () => '',
      type: String,
    },
    initialIsTradeAcquiringEnabled: {
      default: () => false,
      type: Boolean,
    },
    initialLegalName: {
      default: () => '',
      type: String,
    },
    initialLicenseIssuedDate: {
      default: () => '',
      type: String,
    },
    initialLicenseIssuer: {
      default: () => '',
      type: String,
    },
    initialLicenseSerialAndNumber: {
      default: () => '',
      type: String,
    },
    initialPhoneNumber: {
      default: () => '',
      type: String,
    },
    initialSno: {
      default: () => '',
      type: String,
    },
    initialTaxAuthorityCode: {
      default: () => '',
      type: String,
    },
    initialVat: {
      default: () => '',
      type: String,
    },
    /**
     * @description Опции настроек.
     * @type {any}
     */
    settingsOptions: {
      required: true,
      type: Object,
    },
    /**
     * @description Показывать настройки кассы.
     * @type {boolean}
     */
    showCashboxSettings: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Триггер отправки.
     * @type {(number|null)}
     */
    submitTrigger: {
      default: () => null,
      type: Number,
    },
    /**
     * @description Нарушения ограничений.
     * @type {object}
     */
    violationCauses: {
      required: true,
      type: Object,
    },
  },
  watch: {
    isChanged() {
      this.$emit('change', this.isChanged);
    },
    submitTrigger(updatedValue) {
      if (this.disabled !== true && updatedValue) {
        this.onSubmitTrigger();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";

.organization-branch-form-autocomplete::v-deep {
  &.v-text-field--outlined fieldset {
    border: 1px solid var(--border-color);
  }
  &.v-text-field--outlined.v-input--has-state fieldset,
  &.v-text-field--outlined.v-input--is-focused fieldset {
    border: 2px solid var(--action-color);
  }
  & .v-input__slot {
    min-height: 40px;
  }
  & .v-input__append-inner {
    margin-top: 8px;
  }
}

.organization-branch-form {
  display: grid;
  grid-column-gap: 12px;
  grid-row-gap: 15px;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  max-width: 740px;
  & &__cashbox {
    display: grid;
    grid-column-end: 3;
    grid-column-start: 1;
    position: relative;
    row-gap: 12px;
  }
  & &__field {
    grid-column-end: 4;
    grid-column-start: 1;
  }
  & &__select {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  & &__select>label {
    color: var(--unfocus-color);
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    margin-bottom: 3px;
  }
  & &__submit {
    @include buttons.btn-blue(10rem, 3rem, 1rem);
    grid-column-start: -2;
    justify-self: end
  }
  & &__subtitle {
    font-size: 16px;
    font-weight: 600;
    line-height: 19px;
    margin: 20px 0 0 0;
    text-wrap: nowrap;
  }
  & &__three-fields,
  & &__two-fields {
    align-items: baseline;
    display: grid;
    grid-column-end: 4;
    grid-column-start: 1;
    grid-gap: 12px;
  }
  & &__three-fields {
    grid-template-columns: repeat(3, 1fr);
  }
  & &__two-fields {
    grid-template-columns: 2fr 1fr;
  }
}

@media screen and (max-width: 900px) {
  .organization-branch-form {
    width: 100%;
  }
}

@media screen and (max-width: 800px) {
  .organization-branch-form {
    grid-column-gap: 0;
    grid-template-columns: 1fr;
    & &__field {
      grid-column-end: 2;
      grid-column-start: 1;
    }
    & &__three-fields,
    & &__two-fields {
      grid-column-end: 2;
      grid-column-start: 1;
      grid-template-columns: 1fr;
    }
  }
}
</style>
